import React from 'react';
import {
  Avatar,
  Box,
  Card,
  CardActionArea,
  Chip,
  Stack,
  Typography,
} from '@mui/material';
import { Extension as ExtensionIcon } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { BaseFormComponent } from '../types';
import { getFieldFamilyLabel, getFieldFamilyColor } from '../helpers';
import { getFormComponentDisplay } from '../../processDisplay';
import { useTranslation } from 'react-i18next';

interface ComponentCardProps {
  component: BaseFormComponent;
  onOpen: (component: BaseFormComponent) => void;
}

/**
 * Compact form component card — Apple-style whitespace discipline.
 *
 * Design principles:
 * - No shadows; 1px border conveys the card boundary
 * - Small avatar (36px) leaves content room for 13" laptops
 * - 2-line description clamp (no fixed height, no bottom whitespace on short text)
 * - Hover: border color shift only (no shadow jump)
 */
const ComponentCard: React.FC<ComponentCardProps> = ({ component, onOpen }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const display = getFormComponentDisplay(t, component);
  const categoryColor = getFieldFamilyColor(theme, component.category);

  return (
    <Card
      variant="outlined"
      sx={{
        height: '100%',
        width: '100%',
        borderRadius: 2,
        borderColor: 'divider',
        transition: 'border-color 120ms ease',
        '&:hover': {
          borderColor: theme.palette.primary.main,
        },
      }}
    >
      <CardActionArea
        onClick={() => onOpen(component)}
        sx={{ height: '100%', p: 2 }}
      >
        <Stack direction="row" spacing={1.5} alignItems="flex-start">
          <Avatar
            sx={{
              width: 36,
              height: 36,
              bgcolor: categoryColor,
              color: theme.palette.getContrastText(categoryColor),
              flexShrink: 0,
              '& svg': { fontSize: '1.125rem' },
            }}
          >
            {component.icon || <ExtensionIcon />}
          </Avatar>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 600,
                fontSize: '0.9rem',
                lineHeight: 1.3,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
              title={display.label}
            >
              {display.label}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                lineHeight: 1.4,
                mt: 0.25,
                // RESERVE two lines. `-webkit-line-clamp` is a CAP, not a floor, so a
                // one-line description made a shorter card — and the owner's own custom
                // component is the shortest of the sixteen. Within a row `height: '100%'`
                // hid it by stretching; alone on page 2 there is nothing to stretch
                // against, which is where it shows (owner 2026-08-27 20:07).
                minHeight: '2.8em',
              }}
            >
              {display.description}
            </Typography>
            {/* ONE line, always. The `System` chip renders only for seeded components, so
                with `flexWrap` a narrow card wrapped two chips onto a second line for a
                system component and one line for a custom one — the same card, two
                heights. The family chip can ellipsize instead. */}
            <Stack
              direction="row"
              spacing={0.5}
              sx={{ mt: 1, flexWrap: 'nowrap', gap: 0.5, minWidth: 0, height: 18, alignItems: 'center' }}
            >
              <Chip
                label={getFieldFamilyLabel(t, component.category)}
                size="small"
                sx={{
                  height: 18,
                  fontSize: '0.65rem',
                  bgcolor: `${categoryColor}20`,
                  color: categoryColor,
                  fontWeight: 500,
                  border: 'none',
                  minWidth: 0,
                  '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' },
                }}
              />
              {component.isSystem && (
                <Chip
                  label={t('processes:formComponents.dialog.system')}
                  size="small"
                  variant="outlined"
                  sx={{
                    height: 18,
                    fontSize: '0.65rem',
                    borderColor: theme.palette.info.main,
                    color: theme.palette.info.main,
                    fontWeight: 600,
                    flexShrink: 0,
                  }}
                />
              )}
            </Stack>
          </Box>
        </Stack>
      </CardActionArea>
    </Card>
  );
};

export default ComponentCard;
